import { useInvalidate } from '@refinedev/core'
import { useMutation } from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import { productCatalogPatchProductUnhideStatusMutation } from '@repo/sdk'

import { handleSdkError } from '../../providers/sdkErrorUtils'

interface UseUnhideProductReturn {
  unhideProduct: (productId: string) => void
  isUnhiding: boolean
}

export function useUnhideProduct(onSuccess?: () => void): UseUnhideProductReturn {
  const invalidate = useInvalidate()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const { mutate, isPending } = useMutation({
    ...productCatalogPatchProductUnhideStatusMutation(),
    onSuccess: async (_data, variables) => {
      message.success(t('pages.products.messages.unhideSuccess'))
      // refine keys product queries as ['data','default','product-list', …]:
      // a raw ['product-list'] key never matched, so the list stayed stale.
      await invalidate({
        resource: 'product-list',
        invalidates: ['list', 'detail'],
        id: variables.path?.productId,
      })
      onSuccess?.()
    },
    onError: (error) => {
      const { statusCode } = handleSdkError(error)
      if (statusCode === 403) {
        message.error(t('pages.products.messages.broadcastModifyRestricted'))
      } else {
        message.error(t('pages.products.messages.unhideFailed'))
      }
    },
  })

  const unhideProduct = (productId: string) => {
    mutate({ path: { productId } })
  }

  return { unhideProduct, isUnhiding: isPending }
}
